/**
 * ============================================================
 * LAMEEM — MOTION SYSTEM
 * File: assets/abaya-motion.js
 *
 * Single scroll-reveal engine for the whole site. Replaces
 * initScrollReveal() in abaya-luxury.js, which had three faults:
 *
 *   1. It wrote INLINE opacity/transform and injected
 *      `.is-visible { transform: translateY(0) !important }`.
 *      That beat `.card:hover { transform: translateY(-3px) }`,
 *      so every card that had scrolled into view — which is all
 *      of them, since revealing requires scrolling — permanently
 *      lost its hover lift. Verified in Chromium before rewriting.
 *
 *   2. The stagger was `elementIndex * 60ms` computed across the
 *      ENTIRE page. With ~30 revealed elements the last one waited
 *      about 1.9 seconds after entering the viewport before it
 *      began to move, which reads as broken rather than elegant.
 *
 *   3. It was bound to `shopify:section:load`, which fires for ANY
 *      section, so editing the header in the theme editor re-hid
 *      already-revealed content elsewhere on the page.
 *
 * This version sets a class and nothing else, scopes the stagger
 * to each container with a cap, and only re-scans the subtree that
 * actually reloaded.
 * ============================================================
 */

(function () {
  'use strict';

  /* Every homepage section loads this script itself (Dawn's own
     convention), so the same <script src> can appear up to seven
     times on one page. Browsers dedupe repeated stylesheet URLs but
     they DO execute a repeated script once per tag — without this
     guard every listener would be bound seven times over and each
     element observed seven times. Bail after the first run. */
  if (window.__abayaMotionInit) return;
  window.__abayaMotionInit = true;

  var REVEAL = '.ab-reveal';
  var IN = 'ab-in';
  var STAGGER_CAP = 5;   // 6th item onward shares the same delay
  var observer = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function revealNow(el) {
    el.classList.add(IN);
  }

  /**
   * Assign the stagger index PER CONTAINER rather than globally.
   * A container is any [data-ab-stagger]; elements outside one keep
   * whatever --ab-i the template set (usually 0..3 for hero copy).
   */
  function assignStagger(scope) {
    var groups = scope.querySelectorAll('[data-ab-stagger]');
    Array.prototype.forEach.call(groups, function (group) {
      var items = group.querySelectorAll(REVEAL);
      Array.prototype.forEach.call(items, function (el, i) {
        el.style.setProperty('--ab-i', Math.min(i, STAGGER_CAP));
      });
    });
  }

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        revealNow(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    return observer;
  }

  /**
   * @param {Element} scope - only elements inside this subtree are
   *        touched, so a section reload never disturbs the rest of
   *        the page.
   */
  function init(scope) {
    scope = scope || document;

    var els = scope.querySelectorAll(REVEAL);
    if (!els.length) return;

    // Reduced motion: show everything immediately, observe nothing.
    if (prefersReducedMotion()) {
      Array.prototype.forEach.call(els, revealNow);
      return;
    }

    assignStagger(scope);

    var io = getObserver();
    Array.prototype.forEach.call(els, function (el) {
      if (el.classList.contains(IN)) return;   // already revealed
      io.observe(el);
    });
  }

  /* ── STORY "READ MORE" ─────────────────────────────────────
     Progressive enhancement: the extra copy is in the DOM and
     visible without JS, and only becomes collapsible once this
     runs — so the content is never hidden from crawlers or from
     someone with JS disabled. */
  function initStoryToggle(scope) {
    var toggles = (scope || document).querySelectorAll('[data-ab-story-toggle]');
    Array.prototype.forEach.call(toggles, function (btn) {
      if (btn.dataset.abBound) return;
      btn.dataset.abBound = 'true';

      var more = document.getElementById(btn.getAttribute('aria-controls'));
      if (!more) return;

      more.hidden = true;
      btn.hidden = false;
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        more.hidden = open;
        btn.setAttribute('aria-expanded', String(!open));
        btn.textContent = open
          ? btn.getAttribute('data-label-more')
          : btn.getAttribute('data-label-less');
      });
    });
  }

  /* ── BOOT ──────────────────────────────────────────────────
     `no-js` is removed so the CSS fallback (which forces
     .ab-reveal visible) stops applying the moment JS is alive. */
  function boot() {
    document.documentElement.classList.remove('no-js');
    init(document);
    initStoryToggle(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Only re-scan the section that actually reloaded. */
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e && e.target;
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    init(scope);
    initStoryToggle(scope);
  });

  /* In the theme editor a section can be re-selected without a
     reload; make sure anything already on screen is shown. */
  document.addEventListener('shopify:section:select', function (e) {
    var scope = e && e.target;
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    Array.prototype.forEach.call(scope.querySelectorAll(REVEAL), revealNow);
  });
})();
